import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ISSUANCE_TRANSACTION_DEEP_LINK_PARAM } from "../../constants/project-issuance-transaction/ProjectIssuanceTransactionConstants";
import { getProjectIssuances, GetProjectIssuancesResponse, searchIssuanceTransactions } from "../../service/query";
import { Status } from "../../service/Shared";
import { Toast } from "../../widget";

export type DeepLinkedIssuance = GetProjectIssuancesResponse["results"][0];

const UNRESOLVED_MESSAGE = "The transactions this link points to could not be opened.";

/**
 * Resolves the issuance owning the transaction named by an `?issuanceTransactionUuid=` query param, so the
 * project Inventory tab can open that issuance in the Unit transactions panel on arrival. A quote-expiry
 * reminder links to the tab naming its quote, because the notification's own url reaches only the project.
 *
 * Two hops — transaction, then its issuance — because the notification carries the transaction uuid while
 * the panel is opened with an issuance row. Both are read by uuid rather than picked out of the inventory
 * grid, whose rows cover only the page currently loaded and are filtered to Issued and Draft by default: an
 * Inactive issuance would never be found there.
 *
 * The link is consumed once acted on, so the same notification can be opened again: were the param left in
 * place, a second click would navigate to an identical URL, leaving the uuid below unchanged with nothing to
 * re-run and no reopened panel.
 */
export const useDeepLinkedIssuanceTransaction = (projectUuid: string | undefined): DeepLinkedIssuance | undefined => {
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionUuid = searchParams.get(ISSUANCE_TRANSACTION_DEEP_LINK_PARAM);
  const [issuance, setIssuance] = useState<DeepLinkedIssuance>();

  /*
    react-router rebuilds setSearchParams whenever the query string changes, so it is held in a ref to keep
    the effect below keyed on the link alone. As a dependency it would re-resolve on any unrelated param
    change — a filter, an origin — reopening a panel the user had just closed.
  */
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    /*
      Nothing to resolve. The param is consumed below as the issuance is delivered, so its absence here is
      usually this hook's own doing rather than a link being taken away — what was delivered stays put,
      leaving the panel open until its host closes it.
    */
    if (!transactionUuid) {
      return undefined;
    }

    // The project context has not landed yet. The link is left in the URL so it still resolves once it does.
    if (!projectUuid) {
      return undefined;
    }

    // A different link arrived: drop the previous one before the requests start.
    setIssuance(undefined);

    // Flipped by the cleanup below; a response for a superseded param is ignored.
    let isCurrent = true;

    /*
      Take the link out of the URL once it has been acted on, whether or not it resolved — a link that failed
      should retry on the next click rather than sit there. Replace rather than push, since a consumed link
      is not a step to go back to.
    */
    const consumeLink = (): void => {
      setSearchParamsRef.current(
        (previous) => {
          previous.delete(ISSUANCE_TRANSACTION_DEEP_LINK_PARAM);
          return previous;
        },
        { replace: true }
      );
    };

    const loadIssuance = async (): Promise<void> => {
      try {
        const transactionResponse = await searchIssuanceTransactions({
          paging: { afterCursor: null, beforeCursor: null, limit: 1 },
          filter: { results: { uuid: { operator: "eq", value: transactionUuid } } },
        });

        if (!isCurrent) return;

        const transaction =
          transactionResponse.status === Status.Success ? transactionResponse.data?.results[0] : undefined;

        /*
          A link naming a transaction on another project cannot be honoured here: this panel is opened over
          the project in the url, and showing another project's issuance on it would misread.
        */
        if (!transaction || transaction.project.uuid !== projectUuid) {
          consumeLink();
          Toast.error({ message: UNRESOLVED_MESSAGE });
          return;
        }

        const issuanceResponse = await getProjectIssuances({
          paging: { afterCursor: null, beforeCursor: null, limit: 1 },
          request: { projectUuid, groupUuid: null },
          filter: { results: { uuid: { operator: "eq", value: transaction.issuanceUuid } } },
        });

        if (!isCurrent) return;
        consumeLink();

        if (issuanceResponse.status === Status.Success && issuanceResponse.data?.results.length) {
          setIssuance(issuanceResponse.data.results[0]);
        } else {
          Toast.error({ message: UNRESOLVED_MESSAGE });
        }
      } catch {
        if (!isCurrent) return;
        consumeLink();
        Toast.error({ message: UNRESOLVED_MESSAGE });
      }
    };

    loadIssuance();

    return () => {
      isCurrent = false;
    };
  }, [transactionUuid, projectUuid]);

  return issuance;
};
